import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, inject } from '@angular/core';
import { Subscription } from 'rxjs';

import { StepperService } from '../../../core/services/stepper.service'; 


export interface Step {
  icon: string; 
  label: string
}

@Component({
  selector: 'umkt-stepper',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './stepper.component.html',
  styleUrls: ['./stepper.component.scss']
})
export class StepperComponent implements OnInit, OnChanges, OnDestroy {

  @Input() steps: Step[] = [];
  @Input() currentStepIndex: number = 0;
  /**
   * When true, ignores the global StepperService and uses only {@link currentStepIndex} from the parent.
   * Use this for flows outside checkout so the checkout stepper state is not affected.
   */
  @Input() standalone: boolean = false;
  /** Optional denominator for the "n/total" label (e.g. 3 steps with 4 icons). */
  @Input() labelTotalSteps?: number;
  /** Optional numerator for the "n/total" label. */
  @Input() labelCurrentStep?: number;

  public currentStepLabel: string = '';
  public totalSteps: number = 0;
  public currentStepNumber: number = 1;
  private stepSubscription?: Subscription;
  private effectiveStepIndex = 0;
  private stepperService = inject(StepperService);


  ngOnInit(): void {
    this.effectiveStepIndex = this.currentStepIndex;
    if (!this.standalone) {
      this.stepSubscription = this.stepperService.currentStep$.subscribe(index => {
        this.effectiveStepIndex = index;
        this.updateStepInfo();
      });
    } else {
      this.updateStepInfo();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.standalone && changes['steps']) {
      this.stepperService.setSteps(this.steps.length, false);
    }
    if (!this.standalone && changes['currentStepIndex'] && !changes['currentStepIndex'].firstChange) {
      this.effectiveStepIndex = this.currentStepIndex;
    }
    this.updateStepInfo();
  }

  private updateStepInfo(): void {
    const idx = this.standalone ? this.currentStepIndex : this.effectiveStepIndex;

    this.totalSteps = this.labelTotalSteps ?? this.steps.length;
    this.currentStepNumber = this.labelCurrentStep ?? (idx + 1);

    if (this.steps.length > 0 && idx >= 0 && idx < this.steps.length) {
      this.currentStepLabel = this.steps[idx].label;
    } else {
      this.currentStepLabel = '';
    }
  }

  getIconClasses(index: number): object {
    const stepIcon = this.steps[index]?.icon || 'mdn-Icon-engrenagem';
    const idx = this.standalone ? this.currentStepIndex : this.effectiveStepIndex;

    return {
      [stepIcon]: true,
      'mdn-Icon--md': true,
      'step-icon': true,

      'completed': index < idx,

      'active': index === idx,

      'pending': index > idx
    };
  }

  lineIsActive(segmentIndex: number): boolean {
    const idx = this.standalone ? this.currentStepIndex : this.effectiveStepIndex;
    return idx > segmentIndex;
  }

  ngOnDestroy(): void {
    this.stepSubscription?.unsubscribe();
  }
}